/**
 * The reducer: `(state, event) -> state`. Pure, deterministic, and **total**.
 *
 * It never throws and never rejects an event. The app warns rather than blocks,
 * and a correction to a past event can legitimately make later events
 * incoherent, so incoherence is recorded in `state.anomalies` and surfaced in
 * the audit screen rather than thrown. A reducer that threw would turn one bad
 * correction into an unopenable game.
 *
 * Hand mutations are written so that the number of cards added or removed is
 * identical whether the hand is visible or counted. That is what keeps the
 * redaction homomorphism (see `redact.ts`) true.
 */

import type { SeatId, TerritoryId } from '../ids.js';
import type { RecordedEvent } from '../events/events.js';
import type { Card } from '../rules/cards.js';
import { canSee, type Viewer } from './redact.js';
import {
  EMPTY_STATE,
  handSize,
  type Anomaly,
  type GameState,
  type HandView,
  type Seat,
  type TerritoryState,
  type TurnState,
} from './types.js';

export interface ReduceContext {
  readonly viewer: Viewer;
}

export function apply(
  state: GameState,
  event: RecordedEvent,
  ctx: ReduceContext,
): GameState {
  const next = reduce(state, event, ctx);
  // `lastSeq` advances for every event folded, even one that changes nothing
  // for this viewer. Bumping it only on a state change would let a client
  // whose view of an event is a no-op -- identifying a card in a hand it
  // cannot see -- fall behind the sequence the server projected for it.
  const lastSeq = Math.max(state.lastSeq, event.seq);
  if (next === state && next.lastSeq === lastSeq) return state;
  return { ...next, lastSeq };
}

function reduce(state: GameState, event: RecordedEvent, ctx: ReduceContext): GameState {
  switch (event.type) {
    case 'room.created':
      return { ...state, rules: event.payload.rules, mapId: event.payload.mapId };

    case 'seat.claimed': {
      const { seat, name, color, isHost, isNeutral } = event.payload;
      if (state.seats.some((s) => s.id === seat)) {
        return mapSeat(state, seat, (existing) => ({ ...existing, name, color }));
      }
      const added: Seat = {
        id: seat,
        name,
        color,
        isHost,
        isNeutral,
        eliminated: false,
        eliminatedBy: null,
      };
      return {
        ...state,
        seats: [...state.seats, added],
        hands: {
          ...state.hands,
          // A hand starts visible only to its owner; everyone else sees a count.
          [seat]: canSee(ctx.viewer, seat)
            ? { visible: true, slots: [] }
            : { visible: false, count: 0 },
        },
      };
    }

    case 'game.started':
      return { ...state, status: 'setup', phase: 'setup', seatOrder: event.payload.seatOrder };

    case 'setup.assigned': {
      const territories = { ...state.territories };
      for (const assignment of event.payload.assignments) {
        territories[assignment.territory] = {
          owner: assignment.seat,
          armies: assignment.armies,
        };
      }
      return { ...state, territories };
    }

    case 'turn.started':
      return {
        ...state,
        status: 'playing',
        phase: 'reinforceTrade',
        turn: {
          seat: event.payload.seat,
          number: event.payload.number,
          armiesToPlace: 0,
          conqueredThisTurn: false,
          fortifiesUsed: 0,
          pendingBattle: null,
          pendingOccupation: null,
        },
      };

    case 'phase.entered':
      return { ...state, phase: event.payload.phase };

    case 'cards.traded': {
      const { seat, cards, armies, bonusTerritories, bonusArmiesEach } = event.payload;
      let next: GameState = {
        ...state,
        setsTraded: state.setsTraded + 1,
        hands: { ...state.hands, [seat]: removeCards(handOf(state, seat), cards) },
      };
      // The territory bonus is placed on the named territory, not drafted.
      for (const territory of bonusTerritories) {
        next = addArmies(next, territory, bonusArmiesEach, 'cards.traded');
      }
      return withTurn(next, (turn) => ({
        ...turn,
        armiesToPlace: turn.armiesToPlace + armies,
      }));
    }

    case 'reinforcements.calculated':
      return withTurn(state, (turn) => ({
        ...turn,
        armiesToPlace: turn.armiesToPlace + event.payload.breakdown.total,
      }));

    case 'armies.placed': {
      let next = state;
      let total = 0;
      for (const placement of event.payload.placements) {
        next = addArmies(next, placement.territory, placement.count, 'armies.placed');
        total += placement.count;
      }
      return withTurn(next, (turn) => {
        const remaining = turn.armiesToPlace - total;
        return { ...turn, armiesToPlace: Math.max(0, remaining) };
      });
    }

    case 'attack.declared':
      return withTurn(state, (turn) => ({
        ...turn,
        pendingBattle: {
          from: event.payload.from,
          to: event.payload.to,
          attacker: event.payload.attacker,
          defender: event.payload.defender,
          attackerDice: event.payload.attackerDice,
          defenderDice: null,
        },
      }));

    case 'defense.diceChosen':
      return withTurn(state, (turn) =>
        turn.pendingBattle === null
          ? turn
          : {
              ...turn,
              pendingBattle: { ...turn.pendingBattle, defenderDice: event.payload.defenderDice },
            },
      );

    case 'dice.rolled':
      // The roll itself changes nothing; `battle.resolved` carries the losses.
      return state;

    case 'battle.resolved': {
      const battle = state.turn?.pendingBattle;
      if (!battle) {
        return addAnomaly(state, {
          code: 'battle_without_attack',
          message: 'a battle resolved with no attack pending',
        });
      }
      let next = addArmies(state, battle.from, -event.payload.attackerLosses, 'battle');
      next = addArmies(next, battle.to, -event.payload.defenderLosses, 'battle');

      if (!event.payload.conquered) {
        // The attack may continue, but not with the dice it started with: a
        // roll is limited by the armies still standing, and a source that has
        // been ground down cannot keep throwing three. Without this the next
        // roll could cost more armies than the territory has left.
        const remaining = next.territories[battle.from]?.armies ?? 0;
        const allowed = Math.min(
          state.rules?.combat.maxAttackDice ?? 3,
          Math.max(0, remaining - 1),
        );
        // The defender is re-capped for the same reason: a territory ground
        // down to one army cannot keep rolling two dice, and if it did it
        // could lose more armies than it has.
        const defending = next.territories[battle.to]?.armies ?? 0;
        const defence = Math.min(state.rules?.combat.maxDefendDice ?? 2, Math.max(0, defending));
        return withTurn(next, (turn) => ({
          ...turn,
          pendingBattle:
            allowed < 1 || turn.pendingBattle === null
              ? null
              : { ...turn.pendingBattle, attackerDice: allowed, defenderDice: defence },
        }));
      }

      const from = next.territories[battle.from];
      const maximum = Math.max(0, (from?.armies ?? 1) - 1);
      return withTurn(next, (turn) => ({
        ...turn,
        conqueredThisTurn: true,
        pendingOccupation: {
          from: battle.from,
          to: battle.to,
          minimumArmies: Math.min(battle.attackerDice, maximum),
          maximumArmies: maximum,
        },
      }));
    }

    case 'attack.ended':
      return withTurn(state, (turn) => ({ ...turn, pendingBattle: null }));

    case 'territory.occupied': {
      const { from, to, armies } = event.payload;
      const attacker = state.turn?.pendingBattle?.attacker ?? state.turn?.seat ?? null;
      let next = addArmies(state, from, -armies, 'occupation');
      next = setTerritory(next, to, { owner: attacker, armies });
      return withTurn(next, (turn) => ({
        ...turn,
        pendingBattle: null,
        pendingOccupation: null,
      }));
    }

    case 'fortify.performed': {
      const { from, to, armies } = event.payload;
      let next = addArmies(state, from, -armies, 'fortify');
      next = addArmies(next, to, armies, 'fortify');
      return withTurn(next, (turn) => ({ ...turn, fortifiesUsed: turn.fortifiesUsed + 1 }));
    }

    case 'card.drawn':
      return {
        ...state,
        hands: {
          ...state.hands,
          [event.payload.seat]: addCard(handOf(state, event.payload.seat), event.payload.card),
        },
      };

    case 'card.identified': {
      const { seat, slot, card } = event.payload;
      const hand = handOf(state, seat);
      // A viewer who cannot see the hand learns nothing: the count is unchanged.
      if (!hand.visible || card === null) return state;
      if (slot < 0 || slot >= hand.slots.length) {
        return addAnomaly(state, {
          code: 'identify_missing_slot',
          message: `card slot ${slot} does not exist`,
          subject: seat,
        });
      }
      const slots = [...hand.slots];
      slots[slot] = { kind: 'known', card };
      return { ...state, hands: { ...state.hands, [seat]: { visible: true, slots } } };
    }

    case 'player.eliminated': {
      const { seat, by, cardsTransferred, cards } = event.payload;
      const victim = handOf(state, seat);
      const emptied: HandView = victim.visible
        ? { visible: true, slots: [] }
        : { visible: false, count: 0 };

      let next: GameState = {
        ...state,
        hands: {
          ...state.hands,
          [seat]: emptied,
          [by]: addCards(handOf(state, by), cards, cardsTransferred),
        },
      };
      if (cards !== null && cards.length !== cardsTransferred) {
        next = addAnomaly(next, {
          code: 'card_transfer_mismatch',
          message: `${cardsTransferred} cards reported but ${cards.length} supplied`,
          subject: seat,
        });
      }
      return mapSeat(next, seat, (s) => ({ ...s, eliminated: true, eliminatedBy: by }));
    }

    case 'audit.adjusted':
      return setArmies(state, event.payload.territory, event.payload.to, 'audit');

    case 'rules.overridden':
      // A record for the summary, not a change to the game.
      return state;

    case 'turn.ended':
      return { ...state, turn: null };

    case 'game.ended':
      return { ...state, status: 'gameOver', phase: 'gameOver', winner: event.payload.winner };

    case 'correction.applied': {
      const directive = event.payload.directive;
      // `void` and `amend` are resolved by the fold's pre-pass; only `set`
      // takes effect at its own position in the log.
      if (directive.op !== 'set') return state;
      return setTerritory(state, directive.territory, {
        owner: directive.owner,
        armies: directive.armies,
      });
    }

    default: {
      const exhaustive: never = event;
      void exhaustive;
      return state;
    }
  }
}

// --- hands -------------------------------------------------------------
// Every helper adds or removes the same number of cards whether the hand is
// visible or merely counted. Breaking that breaks the redaction homomorphism.

function handOf(state: GameState, seat: SeatId): HandView {
  return state.hands[seat] ?? { visible: false, count: 0 };
}

function addCard(hand: HandView, card: Card | null): HandView {
  if (!hand.visible) return { visible: false, count: hand.count + 1 };
  return {
    visible: true,
    slots: [...hand.slots, card === null ? { kind: 'unknown' } : { kind: 'known', card }],
  };
}

function addCards(hand: HandView, cards: readonly Card[] | null, count: number): HandView {
  if (!hand.visible) return { visible: false, count: hand.count + count };
  const usable = cards !== null && cards.length === count;
  const added = usable
    ? cards.map((card) => ({ kind: 'known', card }) as const)
    : Array.from({ length: count }, () => ({ kind: 'unknown' }) as const);
  return { visible: true, slots: [...hand.slots, ...added] };
}

function removeCards(hand: HandView, cards: readonly Card[]): HandView {
  if (!hand.visible) return { visible: false, count: Math.max(0, hand.count - cards.length) };

  const slots = [...hand.slots];
  for (const card of cards) {
    if (slots.length === 0) break;
    let at = slots.findIndex((slot) => slot.kind === 'known' && slot.card.id === card.id);
    // The card was held but never named -- the common case, since players
    // only identify cards when they matter.
    if (at === -1) at = slots.findIndex((slot) => slot.kind === 'unknown');
    // Last resort: the hand disagrees with the trade. Remove something anyway
    // so the count stays consistent with what every other seat sees.
    if (at === -1) at = 0;
    slots.splice(at, 1);
  }
  return { visible: true, slots };
}

// --- territories -------------------------------------------------------

function setTerritory(state: GameState, id: TerritoryId, next: TerritoryState): GameState {
  return { ...state, territories: { ...state.territories, [id]: next } };
}

function addArmies(
  state: GameState,
  id: TerritoryId,
  delta: number,
  reason: string,
): GameState {
  const territory = state.territories[id];
  if (!territory) {
    return addAnomaly(state, {
      code: 'unknown_territory',
      message: `${reason} referenced unknown territory "${id}"`,
      subject: id,
    });
  }
  const armies = territory.armies + delta;
  const clamped = Math.max(0, armies);
  const next = setTerritory(state, id, { ...territory, armies: clamped });
  return armies === clamped
    ? next
    : addAnomaly(next, {
        code: 'negative_armies',
        message: `${reason} would leave "${id}" at ${armies} armies`,
        subject: id,
      });
}

function setArmies(state: GameState, id: TerritoryId, armies: number, reason: string): GameState {
  const territory = state.territories[id];
  if (!territory) {
    return addAnomaly(state, {
      code: 'unknown_territory',
      message: `${reason} referenced unknown territory "${id}"`,
      subject: id,
    });
  }
  return setTerritory(state, id, { ...territory, armies: Math.max(0, armies) });
}

// --- misc --------------------------------------------------------------

function withTurn(state: GameState, update: (turn: TurnState) => TurnState): GameState {
  if (state.turn === null) {
    return addAnomaly(state, {
      code: 'no_active_turn',
      message: 'a turn action arrived with no turn in progress',
    });
  }
  return { ...state, turn: update(state.turn) };
}

function mapSeat(state: GameState, id: SeatId, update: (seat: Seat) => Seat): GameState {
  return { ...state, seats: state.seats.map((seat) => (seat.id === id ? update(seat) : seat)) };
}

function addAnomaly(state: GameState, anomaly: Anomaly): GameState {
  return { ...state, anomalies: [...state.anomalies, anomaly] };
}

export { EMPTY_STATE, handSize };
